import { useMemo, useState, type ReactNode } from 'react'

import { StatCard } from '@/components/stat/StatCard'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { GuideTab } from '@/features/report/reconcile/GuideTab'
import { KantarsTab } from '@/features/report/reconcile/KantarsTab'
import { StatusIcon } from '@/features/report/reconcile/parts'
import { RECORD_PARAMS } from '@/features/report/reconcile/params'
import { RecordDetailSheet } from '@/features/report/reconcile/RecordDetailSheet'
import { RecordsTab } from '@/features/report/reconcile/RecordsTab'
import { TonnageCard } from '@/features/report/reconcile/TonnageCard'
import type { ReportFilters } from '@/features/report/use-report-filters'
import { formatInteger } from '@/lib/format'
import {
  RECORD_STATUSES,
  summarizeByKantar,
  summarizeTonnage,
  type ReconcileRecord,
  type ReconcileResult,
  type RecordStatus,
} from '@/lib/kantar-dia'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

const t = messages.reconcile
// Hata kodları ve Sahada ayrı sekme değil: aynı kayıtları başka biçimde gösterip kafa
// karıştırıyordu (kullanıcı, 2026-10-18). Her durumun tek yolu üstteki durum kartı.
const TABS = ['records', 'kantars', 'guide'] as const
type Tab = (typeof TABS)[number]
const isTab = (value: string | undefined): value is Tab => TABS.includes(value as Tab)

const KPI_LABEL: Record<RecordStatus, string> = t.kpi
// Simge rengi rozetteki tonla aynı anlamı taşır (StatusBadge varyantları).
const STATUS_TONE: Record<RecordStatus, string> = {
  error: 'text-destructive',
  warning: 'text-warning',
  onsite: 'text-text-muted',
  waiting: 'text-text-muted',
  ok: 'text-success',
  deleted: 'text-text-muted',
}

/**
 * Durum kartı: kayıtları o duruma süzmenin tek yolu. Tekrar tıklayınca süzme kalkar.
 */
function StatusKpi({
  label,
  icon,
  value,
  active,
  tone,
  onClick,
}: {
  label: string
  icon?: ReactNode
  value: number
  active: boolean
  tone?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={`${t.kpiFilter(label, active)} (${formatInteger(value)})`}
      onClick={onClick}
      className="group/kpi focus-visible:ring-ring rounded-surface h-full text-left outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
    >
      {/* uiarc metric-card yüzeyi olduğu gibi; tıklanabilir olduğu için uiarc kartı gibi üzerine gelince güçlenir. */}
      <StatCard
        label={label}
        icon={icon}
        value={formatInteger(value)}
        valueClassName={tone}
        className={cn(
          // Etiketi iki satıra kayan kartta sayı satırdakilerle aynı hizada kalsın: sayı alta yaslanır.
          'flex flex-col justify-between transition-[border-color,box-shadow] max-sm:p-4 max-sm:[&>div:first-child]:mb-6',
          'group-hover/kpi:border-border-strong group-hover/kpi:shadow-raised',
          active && 'border-primary group-hover/kpi:border-primary ring-primary ring-1',
        )}
      />
    </button>
  )
}

export function ReconcileBody({
  result,
  filters,
}: {
  result: ReconcileResult
  filters: ReportFilters
}) {
  const { records } = result
  const [openKey, setOpenKey] = useState<string>()
  // Anahtardan bulunur: canlı güncellemede panel kaydın son hâlini gösterir.
  const opened = openKey === undefined ? undefined : records.find((r) => r.key === openKey)
  const open = (record: ReconcileRecord) => setOpenKey(record.key)

  const tabParam = filters.get('tab')
  const tab: Tab = isTab(tabParam) ? tabParam : 'records'
  const statusFilter = filters.getList(RECORD_PARAMS.status)

  const counts = useMemo(
    () =>
      Object.fromEntries(
        RECORD_STATUSES.map((status) => [
          status,
          records.filter((record) => record.status === status).length,
        ]),
      ) as Record<RecordStatus, number>,
    [records],
  )
  const tonnage = useMemo(() => summarizeTonnage(records), [records])
  const kantars = useMemo(() => summarizeByKantar(records), [records])

  /** Kayıtlar sekmesine geçer; `status` verilmezse bütün kayıt süzmeleri temizlenir. */
  const showRecords = (status?: RecordStatus) =>
    filters.set({
      ...Object.fromEntries(Object.values(RECORD_PARAMS).map((param) => [param, undefined])),
      [RECORD_PARAMS.status]: status && [status],
      tab: undefined,
      page: undefined,
    })
  const statusActive = (status: RecordStatus) =>
    tab === 'records' && statusFilter.length === 1 && statusFilter[0] === status

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 2xl:grid-cols-7">
        <StatusKpi
          label={t.kpi.total}
          value={records.length}
          active={statusFilter.length === 0 && tab === 'records'}
          onClick={() => showRecords()}
        />
        {RECORD_STATUSES.map((status) => (
          <StatusKpi
            key={status}
            label={KPI_LABEL[status]}
            icon={<StatusIcon status={status} className={STATUS_TONE[status]} />}
            value={counts[status]}
            active={statusActive(status)}
            tone={status === 'error' ? 'text-destructive' : undefined}
            onClick={() => showRecords(statusActive(status) ? undefined : status)}
          />
        ))}
      </div>

      <TonnageCard summary={tonnage} recordCount={records.length} />

      <Tabs
        value={tab}
        onValueChange={(value: Tab) =>
          filters.set({ tab: value === 'records' ? undefined : value })
        }
      >
        {/* Sayısız: Kayıtlar süzülünce sekmedeki toplam, tablodaki sayıyla çelişiyordu. */}
        <TabsList variant="line">
          <TabsTrigger value="records">{t.tabs.records}</TabsTrigger>
          <TabsTrigger value="kantars">{t.tabs.kantars}</TabsTrigger>
          <TabsTrigger value="guide">{t.tabs.guide}</TabsTrigger>
        </TabsList>
        <TabsContent value="records">
          <RecordsTab records={records} filters={filters} onOpen={open} />
        </TabsContent>
        <TabsContent value="kantars">
          <KantarsTab summaries={kantars} result={result} />
        </TabsContent>
        <TabsContent value="guide">
          <GuideTab />
        </TabsContent>
      </Tabs>

      <RecordDetailSheet record={opened} onClose={() => setOpenKey(undefined)} />
    </div>
  )
}
